import ddt from '@wix/da-ddt';
import { reduxKey } from './constants';
import {
  ChatStoreSlice,
  OpenChatroom,
  ChatListSubscriptionStatus,
} from './types';
import { ChatWindowMode } from '../ChatWindow/types';
import noop from '../../utils/noop';
import { ChatPanelTab } from '../ChatPanel/ChatPanelTabs/types';
import { createSelector } from 'reselect';

function getState(state): Partial<ChatStoreSlice> {
  if (!state[reduxKey]) {
    return {};
  }
  return state[reduxKey];
}

// Status
export const getChatServiceStatus = state => getState(state).chatServiceStatus;
export const getChatServiceError = state => getState(state).chatServiceError;

// Auth
export const getChatAuthToken = state => getState(state).chatAuthToken;

export const getChatPageType = state => getState(state).chatPageType;
export const getChatPanelOpen = state => getState(state).chatPanelOpen;
export const getChatNewMessageArrived = state =>
  getState(state).newMessageArrived;

export const getShowOverlayChatList = state =>
  getState(state).showOverlayChatList || false;

export const getNewChatParticipants = state =>
  getState(state).newChatWindowParticipants;

export const isChatReduxReady = state => !!state[reduxKey];

// Chat list data
export const getChatListSubscriptionStatus = state =>
  getState(state).chatListSubscriptionStatus;
export const getChatListError = state =>
  getState(state).chatListSubscriptionError;
export const getChatListFetchMore = state =>
  getState(state).chatListSubscription?.fetchMore ?? noop;
export const getChatListCursor = state => getState(state)?.chatsCursor;
export const getChatListChats = state => getState(state).chats;
export const getChatListStatus = state => getState(state).chatsStatus;
export const getChatUnreadCount = state =>
  getState(state).globalUnreadCount?.count || 0;
export const getChatListIsReady = state => {
  const chatListSubscriptionStatus = getChatListSubscriptionStatus(state);
  const chatListStatus = getChatListStatus(state);
  return (
    isChatReduxReady(state) &&
    chatListSubscriptionStatus === ChatListSubscriptionStatus.LISTENING &&
    chatListStatus?.isFetchingRooms === false
  );
};
export const isChatListUsingCache = state => getState(state).chatsUsingCache;
export const getChatListActiveTab = state =>
  getState(state).chatListTab || ChatPanelTab.GENERAL;

// Chat requests
export const getChatRequestsSubscriptionStatus = state =>
  getState(state).chatRequestsSubscriptionStatus;
export const getChatRequestsSubscriptionError = state =>
  getState(state).chatRequestsSubscriptionError;
export const getChatRequestsFetchMore = state =>
  getState(state).chatRequestsSubscription?.fetchMore ?? noop;
export const getChatRequestsCursor = state =>
  getState(state)?.chatRequestsCursor;
export const getChatRequests = state => getState(state).chatRequests;
export const getChatRequestsStatus = state =>
  getState(state).chatRequestsStatus;
export const isChatRequestsUsingCache = state =>
  getState(state).chatRequestsUsingCache;

// Chatrooms
export const getOpenChatrooms = state => getState(state).chatrooms;
export const getOpenChatroomsOrder = state =>
  getState(state).openChatroomsOrder;

/** Pulls a full list of participants we need to send a new chat request. This includes the DM participant's ID. */
export const getChatroomNewGroupChatParticipants = (
  state,
  chatroomId: string
) => {
  const participants =
    getOpenChatrooms(state)?.[chatroomId]?.newChatParticipants ?? [];
  const currentParticipant =
    getOpenChatrooms(state)?.[chatroomId]?.room?.otherParticipantId;
  if (currentParticipant) {
    participants.push({ id: currentParticipant, name: '' });
  }
  return participants;
};

export const getIsChatroomOpen = (state, chatroomId: string): boolean => {
  const openChatroomIds = getOpenChatroomsOrder(state);
  return !!openChatroomIds?.includes(chatroomId);
};
export const getNewChatRequests = state => getState(state).newChatRequests;

export const getNewChatRequestsList = createSelector(
  getNewChatRequests,
  items => Object.values(items ?? {})
);

export const getPendingNewChatMessage = (state, requestId: string) =>
  getState(state).newChatRequests?.[requestId]?.pendingMessage;

const defaultChatroomValue = {} as OpenChatroom;
export const getChatroom = (state, chatId): OpenChatroom => {
  const chatrooms = getState(state).chatrooms;
  if (chatrooms && chatrooms.hasOwnProperty(chatId)) {
    return chatrooms[chatId];
  } else {
    ddt.log(
      'chat',
      `We tried to get a chatroom that wasn't inside of our store -- chatroom id [${chatId}]`
    );
    return defaultChatroomValue;
  }
};

/** Return a list of chatroom ids of open chat windows that are currently minimized */
export const getMinimizedChatWindows = (state): string[] => {
  const chatrooms = getOpenChatrooms(state) || {};
  const openChatroomsOrder = getOpenChatroomsOrder(state) || [];
  return Object.keys(chatrooms).filter(id => {
    const chatroom = chatrooms[id];
    // because a closed room's data persists in `chatrooms` even after it's closed,
    // we also need to check this array to see if it's been closed
    const isOpen = openChatroomsOrder.includes(id);
    return chatroom && isOpen && chatroom.mode === ChatWindowMode.MINIMIZED;
  });
};
export const getChatsWithNewMsgs = state => getState(state).chatsWithNewMsgs;
export const getChatWindowFooterHeight = (state, chatId) =>
  getState(state).footerHeights?.[chatId];

export const getNextShouldOpenRequestIds = state =>
  getState(state).nextShouldOpenRequestIds ?? [];

export const getChatEnvironment = state => state.environment?.type;

export const getChatWorker = state => getState(state).chatWorker;
export const getChatWorkerStatus = state => getState(state).chatWorkerStatus;
export const getChatWorkerIdleStatus = state =>
  getState(state).chatWorkerIdleStatus;

export const getAltchaToken = state =>
  getState(state).altcha?.verificationToken;
